<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type { Rules } from "async-validator";
import { type ModelRef, ref, type Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useFetchSectors } from "~/modules/sector/composables/useFetchSectors";
import { useCreateUser } from "~/modules/user/composables/useCreateUser";
import { useFetchUserGroups } from "~/modules/user/composables/useFetchUserGroups";
import { useUpdateUser } from "~/modules/user/composables/useUpdateUser";
import type { UserCreateDto } from "~/modules/user/models/UserCreateDto.interface";
import type { UserDetails } from "~/modules/user/models/UserDetails.interface";
import type { UserProfile } from "~/modules/user/models/UserProfile.interface";
import type { UserSessionSector } from "~/modules/user/models/UserSessionSector.interface";
import type { UserUpdateDto } from "~/modules/user/models/UserUpdateDto.interface";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";
import { getUserValidationRules } from "~/modules/user/utils/userValidationRules";
import { setLocaleTag } from "~/plugins/i18n";

const { t } = useI18n();
const serialize = useSerialize();
const { can } = useAcl();

const { userSession } = useUserSession();

const props = defineProps<{
  show: boolean;
  businessUnitCode?: number;
  organizationCode?: number;
  organizationType?: OrganizationType;
  userLogin?: string;
  selectedUser?: UserDetails;
  currentUser?: UserDetails;
  isLoadingGetSelectedUser?: boolean;
  useExternalIdp?: boolean;
  profiles?: UserProfile[];
  canUpdateUser?: boolean;
}>();

const emits = defineEmits(["success"]);

const { fetchSectors } = useFetchSectors(props.organizationCode);

const {
  createUser,
  isLoading: isLoadingCreate,
  error,
} = useCreateUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});

const {
  updateUser,
  isLoading: isLoadingUpdate,
  error: errorUpdate,
} = useUpdateUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});

const {
  resultsFetchAll: groupsResult,
  fetchAll: groupsFetchAll,
  isLoadingAllGroups: isLoadingGetGroups,
} = useFetchUserGroups({
  organizationCode: props.organizationCode,
  searchMode: SearchMode.FILTER_SEARCH,
});

const edited: ModelRef<boolean> = defineModel<boolean>("edited", {
  required: true,
  default: false,
});

const userForm: Partial<UserCreateDto> = reactive({
  email: "",
  firstName: "",
  lastName: "",
  language: "",
  login: "",
  profile: "",
  groups: [],
  sectors: [],
});
const userFormRef: Ref<HTMLFormElement | undefined | null> = ref();

let initFormStringified: Ref<string> = ref(serialize(userForm));
const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(userForm) !== initFormStringified.value,
);
const formRules: Rules = {
  ...getUserValidationRules(t),
  profile: [
    {
      required: true,
      message: t("user.profileRequired"),
    },
  ],
};
const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => userFormRef.value?.isValid,
);
const showGroups: ComputedRef<boolean> = computed(
  () => props.organizationType === OrganizationType.OPERATOR,
);
const showSectors: ComputedRef<boolean> = computed(() => {
  return (
    props.organizationType === OrganizationType.OPERATOR &&
    can(ACLRoles.CAN_READ_SECTOR)
  );
});

function onSuccess() {
  showNotificationSuccess(t(NotificationKey.saveSuccess));
  edited.value = true;
  emits("success");
}

async function onSubmit() {
  if (isFormValid.value) {
    const userRequest: UserUpdateDto = {
      firstName: userForm.firstName!,
      lastName: userForm.lastName!,
      email: userForm.email!,
      active: true,
      profile: userForm.profile!,
      language: userForm.language!,
    };
    if (showGroups.value) {
      userRequest.groups = toRaw(userForm.groups);
    }

    if (showSectors.value) {
      userRequest.sectors = userForm.sectors;
    }

    if (props.userLogin) {
      await updateUser(props.userLogin!, userRequest);
      if (errorUpdate.value) {
        errorUpdate.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();

        // Check if the updated user is the current one
        if (props.userLogin === userSession?.login) {
          // Update the locale tag if the language has changed
          if (userRequest.language !== props.currentUser?.language) {
            setTimeout(() => setLocaleTag(userRequest.language), 0);
          }

          // Refresh the sector store
          if (props.organizationCode) {
            await fetchSectors(true);
          }
        }
      }
    } else {
      const userRequestCreate: UserCreateDto = {
        ...userRequest,
        login: userForm.login!,
      };
      await createUser(userRequestCreate);
      if (error.value) {
        error.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();
      }
    }
  }
}

function clearForm() {
  userForm.email = "";
  userForm.firstName = "";
  userForm.lastName = "";
  userForm.language = "";
  userForm.login = "";
  userForm.profile = "";
  userForm.groups = [];
  userForm.sectors = [];
  initFormStringified.value = serialize(userForm);
  setTimeout(() => {
    userFormRef.value?.clearValidate();
  }, 0);
}

async function initializeUserForm() {
  if (props.userLogin && props.selectedUser) {
    userForm.login = props.userLogin;
    userForm.lastName = props.selectedUser.lastName;
    userForm.firstName = props.selectedUser.firstName;
    userForm.email = props.selectedUser.email;
    userForm.profile = props.selectedUser.profile.id;
    userForm.language = props.selectedUser.language;
    userForm.groups =
      props.selectedUser.groups?.map((group) => group.name) ?? [];
    userForm.sectors =
      props.selectedUser.sectors?.map((sector: UserSessionSector) =>
        sector.code.toString(),
      ) ?? [];
    initFormStringified.value = serialize(userForm);
  }
}

function reset() {
  // awaits can be avoided here
  if (props.show) {
    if (showGroups.value) {
      groupsFetchAll();
    }

    if (props.userLogin) {
      initializeUserForm();
    }
  }
}

onMounted(() => {
  reset();
});

watch(() => [props.userLogin, props.show, props.selectedUser] as const, reset);

defineExpose({
  isLoadingCreate,
  isLoadingUpdate,
  formHasChanged,
  clearForm,
  isFormValid,
  onSubmit,
});
</script>

<template>
  <lxc-form
    ref="userFormRef"
    :rules="formRules"
    :model="userForm"
    @submit.prevent="onSubmit"
  >
    <h6 class="mb-4 font-bold">
      {{ t("user.create.information") }}
    </h6>
    <div class="grid gap-x-8 grid-cols-2">
      <lxc-form-item
        :label="t('user.lastName')"
        prop="lastName"
        class="basis-1/2"
        :disabled="useExternalIdp || !canUpdateUser"
      >
        <lxc-input v-model="userForm.lastName" type="text" />
      </lxc-form-item>
      <lxc-form-item
        :label="t('user.firstName')"
        prop="firstName"
        class="basis-1/2"
        :disabled="useExternalIdp || !canUpdateUser"
      >
        <lxc-input v-model="userForm.firstName" type="text" />
      </lxc-form-item>
      <lxc-form-item
        :label="t('user.login')"
        prop="login"
        class="basis-1/2"
        :disabled="!!userLogin || !canUpdateUser"
      >
        <lxc-input v-model="userForm.login" type="text" />
      </lxc-form-item>
      <lxc-form-item
        :label="t('user.email')"
        prop="email"
        class="basis-1/2"
        :disabled="useExternalIdp || !canUpdateUser"
      >
        <lxc-input v-model="userForm.email" type="email" />
      </lxc-form-item>
      <lxc-form-item
        :label="t('user.language')"
        prop="language"
        class="basis-1/2"
        :disabled="!canUpdateUser"
      >
        <language-component v-model="userForm.language" />
      </lxc-form-item>
      <lxc-form-item
        :label="t('user.profile')"
        prop="profile"
        class="pt-2 col-start-1"
        :disabled="!canUpdateUser"
      >
        <lxc-select
          v-model="userForm.profile"
          label-class="pb-2 font-semibold inline-block text-base !text-gray-900"
          select-filter
          :select-search-placeholder="t('user.profilePlaceholder')"
          :select-no-result-text="t('input.select.search.noResults')"
        >
          <lxc-option
            v-for="item in profiles"
            :key="item.id"
            :label="getProfileLabel(item)"
            :value="item.id"
            :disabled="!item.isAuthorized"
          />
        </lxc-select>
      </lxc-form-item>
      <lxc-form-item
        v-if="showGroups"
        :label="t('user.groups')"
        prop="groups"
        class="pt-2 col-start-1"
        :disabled="!canUpdateUser"
      >
        <lxc-select
          v-model="userForm.groups"
          label-class="pb-2 font-semibold inline-block text-base !text-gray-900"
          multiple
          select-filter
          selected-as-tag
          :select-all-label="t('user.form.userGroups.selectAllLabel')"
          :select-search-placeholder="t('user.groupsSearchPlaceholder')"
          :select-no-result-text="t('input.select.search.noResults')"
          :is-loading-options="isLoadingGetGroups"
          :is-loading-selected="isLoadingGetSelectedUser"
        >
          <lxc-option
            v-for="item in groupsResult"
            :key="item.name"
            :label="item.name"
            :value="item.name"
          >
            {{ item.name }}
          </lxc-option>
        </lxc-select>
      </lxc-form-item>
      <lxc-form-item
        v-if="showSectors"
        :label="t('user.sectors')"
        label-class="pb-2 font-semibold inline-block text-base !text-gray-900"
        prop="sectors"
        class="pt-2 col-start-1"
        display-label
      >
        <user-sector-selection
          v-model="userForm.sectors"
          :disabled="!canUpdateUser"
        />
      </lxc-form-item>
    </div>
  </lxc-form>
</template>
